import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscriptionSuccess = () => {
  const { t } = useTranslation();
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [attempts, setAttempts] = useState(0);
  const pollStarted = useRef(false);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && user) {
      if (pollStarted.current) return;
      pollStarted.current = true;
      pollPaymentStatus(sessionId);
    } else if (!user) {
      // Wait for auth to load
      const timer = setTimeout(() => {
        if (!user) {
          navigate('/login');
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [searchParams, user]);

  const pollPaymentStatus = async (sessionId, attemptCount = 0) => {
    const maxAttempts = 15;
    const pollInterval = 2000;

    if (attemptCount >= maxAttempts) {
      setStatus('error');
      toast.error(t('subscription_success.timeout_error'));
      return;
    }

    setAttempts(attemptCount);

    try {
      // ── Commandment VI: GET /checkout/status is read-only ──
      const response = await axios.get(`${API}/subscription/checkout/status/${sessionId}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });

      if (response.data.payment_status === 'paid') {
        // Writer: persist subscription + payment event exactly once
        await axios.post(`${API}/subscription/confirm`, { session_id: sessionId }, {
          headers: getAuthHeaders(), withCredentials: true,
        }).catch(() => { /* idempotent — ignore if already confirmed */ });
        if (searchParams.get('return_to') === 'strategy-advisor') {
          navigate('/strategy-advisor?welcome=1', { replace: true });
          return;
        }
        setStatus('success');
        toast.success(t('subscription_success.success_desc'));
        setTimeout(() => navigate('/dashboard'), 2000);
        return;
      } else if (response.data.status === 'expired') {
        setStatus('error');
        toast.error(t('subscription_success.session_expired'));
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attemptCount + 1), pollInterval);
    } catch (error) {
      setTimeout(() => pollPaymentStatus(sessionId, attemptCount + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 animate-spin text-emerald-500 mx-auto mb-6" />
              <h2 className="text-2xl font-bold mb-2">{t('subscription_success.verifying_title')}</h2>
              <p className="text-gray-600 mb-4">
                {t('subscription_success.verifying_desc')}
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-emerald-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${(attempts / 15) * 100}%` }}
                />
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-6" />
              <h2 className="text-2xl font-bold mb-2 text-emerald-600">{t('subscription_success.success_title')}</h2>
              <p className="text-gray-600 mb-6">
                {t('subscription_success.success_desc')}
              </p>
              <Button 
                onClick={() => navigate('/dashboard')}
                className="bg-emerald-600 hover:bg-emerald-500"
              >
                {t('subscription_success.go_to_dashboard')}
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
              <h2 className="text-2xl font-bold mb-2 text-red-600">{t('subscription_success.error_title')}</h2>
              <p className="text-gray-600 mb-6">
                {t('subscription_success.error_desc')}
              </p>
              <div className="flex gap-3 justify-center">
                <Button 
                  variant="outline"
                  onClick={() => navigate('/pricing')}
                >
                  {t('subscription_success.try_again')}
                </Button>
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="bg-emerald-600 hover:bg-emerald-500"
                >
                  {t('subscription_success.go_to_dashboard')}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;
